import React, { useState } from 'react';
import { Search, SlidersHorizontal, RotateCcw, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface FilterBarProps {
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  searchPlaceholder?: string;
  children?: React.ReactNode;
  moreFiltersContent?: React.ReactNode;
  activeMoreFiltersCount?: number;
  isMoreFiltersOpen?: boolean;
  onToggleMoreFilters?: () => void;
  onReset?: () => void;
  showReset?: boolean;
  yearSelector?: React.ReactNode;
  className?: string;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Search records...',
  children,
  moreFiltersContent,
  activeMoreFiltersCount = 0,
  isMoreFiltersOpen: controlledMoreOpen,
  onToggleMoreFilters,
  onReset,
  showReset = true,
  yearSelector,
  className,
}) => {
  const [internalMoreOpen, setInternalMoreOpen] = useState(false);
  const isMoreOpen = controlledMoreOpen !== undefined ? controlledMoreOpen : internalMoreOpen;

  const handleToggleMore = () => {
    if (onToggleMoreFilters) {
      onToggleMoreFilters();
    } else {
      setInternalMoreOpen((prev) => !prev);
    }
  };

  const hasSearch = onSearchChange !== undefined;

  return (
    <div
      className={cn(
        'bg-white p-3 sm:p-3.5 rounded-xl border border-slate-200 shadow-xs space-y-3',
        className
      )}
    >
      {/* Optional Year Selector Sub-Row */}
      {yearSelector && (
        <div className="pb-2 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          {yearSelector}
        </div>
      )}

      {/* Primary Filter Row */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2.5">
        {/* Left / Search & Primary Controls */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1 flex-wrap">
          {/* Search Input */}
          {hasSearch && (
            <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                type="text"
                value={searchTerm || ''}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-9 pr-8 h-9 text-xs bg-slate-50/50 border-slate-300 focus:bg-white rounded-lg"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => onSearchChange('')}
                  aria-label="Clear search input"
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-full"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}

          {/* Child Selectors / Quick Filter Chips / Comboboxes */}
          {children}
        </div>

        {/* Right / Secondary Controls (More Filters + Reset) */}
        <div className="flex items-center gap-2 justify-end flex-shrink-0">
          {/* More Filters Toggle */}
          {moreFiltersContent && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleToggleMore}
              className={cn(
                'h-9 text-xs font-semibold gap-1.5 border-slate-300',
                isMoreOpen && 'bg-slate-100 border-slate-400 text-slate-900',
                activeMoreFiltersCount > 0 && 'border-emerald-600 text-emerald-800 bg-emerald-50/60'
              )}
            >
              <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
              <span>More Filters</span>
              {activeMoreFiltersCount > 0 && (
                <span className="px-1.5 py-0.2 text-[10px] font-bold bg-emerald-700 text-white rounded-full">
                  {activeMoreFiltersCount}
                </span>
              )}
              <ChevronDown
                className={cn(
                  'w-3.5 h-3.5 transition-transform duration-150',
                  isMoreOpen && 'transform rotate-180'
                )}
              />
            </Button>
          )}

          {/* Reset Filters Button */}
          {showReset && onReset && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="h-9 text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100 gap-1"
              title="Reset all filters"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Reset</span>
            </Button>
          )}
        </div>
      </div>

      {/* Expandable "More Filters" Secondary Drawer */}
      {moreFiltersContent && isMoreOpen && (
        <div className="pt-3 border-t border-slate-100 bg-slate-50/60 -mx-3 -mb-3 p-3 sm:p-3.5 rounded-b-xl animate-in fade-in-0 slide-in-from-top-1 duration-150">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {moreFiltersContent}
          </div>
        </div>
      )}
    </div>
  );
};
